import React, { useState, useEffect } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import { fetchTopics, deleteTopic, ForumTopicListResponse } from '../../services/forumService';
import { useAuth } from '../../context/AuthContext';
import { useScreenType } from '../../hooks/useScreenType';

import ForumDesktop from './ForumDesktop';
import ForumMobile from './ForumMobile';

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
    GENERAL: { label: 'Général', color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)', icon: '💬' },
    MACROECO: { label: 'Macroéconomie', color: '#f472b6', bg: 'rgba(244, 114, 182, 0.12)', icon: '🌍' },
    BRVM: { label: 'BRVM', color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', icon: '📈' },
    ACTION_SPECIFIQUE: { label: 'Action', color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)', icon: '🏢' },
};

const ForumScreen: React.FC = () => {
    const [data, setData] = useState<ForumTopicListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('ALL');
    const { user } = useAuth();
    const { isMobile } = useScreenType();

    useEffect(() => { loadTopics(); }, []);

    const loadTopics = async () => {
        try {
            setLoading(true);
            const res = await fetchTopics(0, 50);
            setData(res);
        } catch (err) {
            console.error("Failed to load topics", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTopic = async (e: React.MouseEvent, topicId: number) => {
        e.stopPropagation();
        if (!window.confirm("Voulez-vous vraiment supprimer ce sujet et toutes les réponses ?")) return;
        try {
            await deleteTopic(topicId);
            setData(prev => prev ? {
                ...prev,
                topics: prev.topics.filter(t => t.id !== topicId),
                total_count: prev.total_count - 1
            } : null);
        } catch (err: any) {
            alert(err.message || "Erreur lors de la suppression du sujet");
        }
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return "À l'instant";
        if (diffMin < 60) return `il y a ${diffMin} min`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `il y a ${diffH}h`;
        const diffD = Math.floor(diffH / 24);
        if (diffD < 7) return `il y a ${diffD}j`;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <SiteLayout>
            <div className='content-area'>
                {isMobile ? (
                    <ForumMobile 
                        data={data}
                        loading={loading}
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        handleDeleteTopic={handleDeleteTopic}
                        formatDate={formatDate}
                        CATEGORY_CONFIG={CATEGORY_CONFIG}
                        user={user}
                    />
                ) : (
                    <ForumDesktop 
                        data={data}
                        loading={loading}
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        handleDeleteTopic={handleDeleteTopic}
                        formatDate={formatDate}
                        CATEGORY_CONFIG={CATEGORY_CONFIG}
                        user={user}
                    />
                )}
            </div>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </SiteLayout>
    );
};

export default ForumScreen;
